import { turnLabel, type TurnEntry } from './turn-nav.js';

/**
 * The unified turn rail for an indexed (paged) session: one list covering
 * every turn in the session, numbered continuously by true session order.
 *
 * The session index's turn-opening `prompt` rows are the source of truth for
 * what the turns are and what number each carries. Loaded turns — the ones
 * `buildTurnNav` derived from the mounted transcript — are reconciled against
 * those rows by the Axon sequence of their opening prompt event: a matched
 * row renders as a live entry (status glyph, anchor to scroll to), an
 * unmatched row renders as a muted index-only entry whose selection loads
 * history toward it.
 */

/** A `prompt` row from the agent's session index, in session order. */
export interface SessionIndexTurn {
  /** Axon sequence of the prompt event the row is anchored at. */
  seqNumber: number;
  /** The prompt's first characters, pre-truncated at write time. */
  text: string;
  /** False for mid-turn steers, which are prompts but not turns. */
  opensTurn: boolean;
}

export type UnifiedTurnEntry =
  | {
      kind: 'loaded';
      /**
       * Position in the whole session. Null for a mid-turn steer: the index
       * is canonical about what counts as a turn, and steers do not — they
       * keep their row (status, anchor) but no number, so the numbering
       * cannot drift from the index's.
       */
      number: number | null;
      turn: TurnEntry;
    }
  | {
      kind: 'unloaded';
      number: number;
      /** What selecting the row loads history toward. */
      seqNumber: number;
      label: string;
    };

export interface UnifiedTurnRail {
  entries: UnifiedTurnEntry[];
  /** Turns in the whole session — the highest number assigned. */
  totalTurns: number;
}

/**
 * Merge the session index's prompt rows with the turns loaded in the
 * transcript into one continuously numbered list.
 *
 * Reconciliation rules, in order:
 * - Every `opensTurn` index row gets one entry and the next number, in
 *   session order. A row whose sequence matches a loaded turn renders
 *   loaded; the rest render unloaded (this also covers loaded-but-unmounted
 *   turns the transcript window is holding out of the DOM — selecting one
 *   drives the same jump flow, which reveals and scrolls).
 * - A loaded turn the index marks `opensTurn: false` is a steer: it sits
 *   after the entry for the turn it steered — the last indexed turn at or
 *   below its sequence — but is not numbered, so index numbering stays
 *   canonical.
 * - A loaded turn the index does not know — no sequence, or a sequence past
 *   the last indexed row (the index trails the live stream by its refetch
 *   interval) — is a fresh turn: it lists after every indexed turn and
 *   continues the numbering. A loaded turn inside the indexed range that
 *   still matched nothing is placed by sequence like a steer, unnumbered
 *   rather than double-counted.
 */
export function buildUnifiedTurnRail(
  indexTurns: SessionIndexTurn[],
  loadedTurns: TurnEntry[],
): UnifiedTurnRail {
  const loadedBySeq = new Map<number, TurnEntry>();
  for (const turn of loadedTurns) {
    if (turn.seqNumber !== undefined) loadedBySeq.set(turn.seqNumber, turn);
  }
  const openingSeqs = new Set(
    indexTurns.filter((row) => row.opensTurn).map((row) => row.seqNumber),
  );
  const firstOpeningSeq =
    indexTurns.find((row) => row.opensTurn)?.seqNumber ?? Number.POSITIVE_INFINITY;
  const lastIndexedSeq = indexTurns.length
    ? indexTurns[indexTurns.length - 1].seqNumber
    : Number.NEGATIVE_INFINITY;

  // Loaded turns the index walk below will not place, split by where they
  // belong. Sequence, not transcript adjacency, decides the position: the
  // loaded window may hold no indexed turn at all, and a fresh turn must
  // still come after every indexed one.
  // - `inRange` — steers, and in-range turns that match no row — go back in
  //   by sequence, each after the last indexed turn at or below it (a steer
  //   lands after its parent turn's row).
  // - `fresh` — past the index, or with no sequence at all — append after
  //   the indexed tail in transcript order and continue the numbering.
  // - `appendix` — before every indexed turn, so nothing covers them — just
  //   append, unnumbered.
  const inRange: TurnEntry[] = [];
  const fresh: TurnEntry[] = [];
  const appendix: TurnEntry[] = [];
  for (const turn of loadedTurns) {
    if (turn.seqNumber !== undefined && openingSeqs.has(turn.seqNumber)) continue;
    if (turn.seqNumber === undefined || turn.seqNumber > lastIndexedSeq) fresh.push(turn);
    else if (turn.seqNumber < firstOpeningSeq) appendix.push(turn);
    else inRange.push(turn);
  }
  inRange.sort((a, b) => a.seqNumber! - b.seqNumber!);

  const entries: UnifiedTurnEntry[] = [];
  let number = 0;
  // One pointer over the sequence-sorted in-range turns keeps the merge
  // linear however much history the reader has loaded.
  let nextInRange = 0;
  for (const row of indexTurns) {
    if (!row.opensTurn) continue;
    while (nextInRange < inRange.length && inRange[nextInRange].seqNumber! < row.seqNumber) {
      entries.push({ kind: 'loaded', number: null, turn: inRange[nextInRange] });
      nextInRange += 1;
    }
    number += 1;
    const match = loadedBySeq.get(row.seqNumber);
    if (match) {
      entries.push({ kind: 'loaded', number, turn: match });
    } else {
      entries.push({
        kind: 'unloaded',
        number,
        seqNumber: row.seqNumber,
        label: turnLabel(row.text),
      });
    }
  }
  // In-range turns at or past the newest indexed turn — a steer on it, say.
  for (; nextInRange < inRange.length; nextInRange += 1) {
    entries.push({ kind: 'loaded', number: null, turn: inRange[nextInRange] });
  }
  for (const turn of appendix) {
    entries.push({ kind: 'loaded', number: null, turn });
  }
  for (const turn of fresh) {
    number += 1;
    entries.push({ kind: 'loaded', number, turn });
  }

  return { entries, totalTurns: number };
}
